import React from 'react';
import BottomNav from './BottomNav';
import SceneObjects from './SceneObjects';

/**
 * The signed-in frame for Arcanum 2.0. Built in Phase 1, mounted in Phase 3.
 *
 * It owns three things and nothing else: the page background (artwork plus
 * the runtime scrim that keeps text legible over it), safe-area padding at the
 * top, and space at the bottom so content never slides under the navigation.
 *
 * Artwork is imagery only. Everything readable or pressable is live DOM on top
 * of it; nothing is ever baked into the picture.
 *
 * On a wide screen the portrait artwork is centred on obsidian rather than
 * stretched: it was painted for phones, and cover-cropping it to 1920px would
 * upscale it roughly twofold and soften it. Approved as decision 6.
 *
 * Authentication and recovery screens never use this -- they must not show the
 * signed-in navigation.
 *
 * `fill` is for a page that manages its own scrolling, like a conversation:
 * the frame is then exactly one screen tall (less the navigation) and the
 * page lays itself out inside it, composer at the bottom.
 *
 * `focusX` moves which part of a cropped artwork stays on screen (0 = left
 * edge, 0.5 = centre). `scene` makes painted objects pressable: see
 * SceneObjects, which uses the same frame and the same focus so the two can
 * never disagree. `artAspect` is the artwork's width / height.
 */
const AppShell = ({
  user, background, scrim = 0.34, activeKey, fill = false,
  focusX = 0.5, scene, artAspect, children,
}) => (
  <div className="relative min-h-screen bg-background text-foreground">
    {background && (
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0"
        style={{
          backgroundImage:
            `linear-gradient(rgba(7,9,10,${scrim}), rgba(7,9,10,${scrim})), url(${background})`,
          backgroundSize: 'auto 100%, cover',
          backgroundPosition: `center, ${focusX * 100}% center`,
          backgroundRepeat: 'no-repeat',
          // Portrait art centred on obsidian past a phone's width.
          maxWidth: 'min(100vw, 56.25vh)',
          margin: '0 auto',
        }}
      />
    )}
    {background && scene && scene.length > 0 && (
      <SceneObjects aspect={artAspect} focusX={focusX} objects={scene} art={background} scrim={scrim} />
    )}
    <main
      className={fill ? 'relative flex flex-col h-screen-dvh' : 'relative'}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        // Navigation height (min 44px target + padding) plus the safe area.
        paddingBottom: 'calc(64px + env(safe-area-inset-bottom))',
      }}
    >
      {children}
    </main>
    <BottomNav user={user} activeKey={activeKey} />
  </div>
);

/**
 * The frame a signed-in page sits in. Every role gets the 2.0 shell; what
 * its navigation offers comes from navFor(), so an operator's frame is built
 * from an operator's destinations rather than a Founder's with parts hidden.
 */
export const Frame = ({
  user, background, scrim, activeKey, fill = false, focusX, scene, artAspect, children,
}) => (
  <AppShell
    user={user} background={background} scrim={scrim} activeKey={activeKey} fill={fill}
    focusX={focusX} scene={scene} artAspect={artAspect}
  >
    {children}
  </AppShell>
);

export default AppShell;
